import { useCallback, useEffect, useRef, useState } from "react";
import { Button, ButtonVariant, Tooltip } from "@merge-api/merge-javascript-shared";
import copy from "copy-to-clipboard";
import { Check, Copy } from "lucide-react";

interface CopyButtonProps {
  text: string;
  label: string;
  // renders an icon-only button with a tooltip instead of a labeled button
  iconOnly?: boolean;
}

// Button that copies `text` to the clipboard and briefly confirms with "Copied!".
const CopyButton = ({ text, label, iconOnly = false }: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleCopy = useCallback(() => {
    copy(text);
    setCopied(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 1500);
  }, [text]);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const icon = copied ? <Check size={12} /> : <Copy size={12} />;

  if (iconOnly) {
    return (
      <Tooltip title={copied ? "Copied!" : label}>
        <Button
          size="sm"
          variant={ButtonVariant.IconShadowHidden}
          ariaLabel={label}
          onClick={handleCopy}
        >
          {icon}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button size="sm" variant={ButtonVariant.TertiaryWhite} leftIcon={icon} onClick={handleCopy}>
      {copied ? "Copied!" : label}
    </Button>
  );
};

export default CopyButton;
